import { ISectionLogger } from "Logging";

import { DIContainer } from "../DIContainer";
import { InvalidArgumentException, InvalidOperationException } from "../Exceptions";
import { CancellationTokenSource } from "../Promises/CancellationTokenSource";
import { Deferred, PromiseUtils } from "../Promises/PromiseUtils";
import { ArrayUtils } from "../Utils/ArrayUtils";
import { EnumUtils } from "../Utils/EnumUtils";
import { IRightsContext } from "./IRightsContext";
import { IRightsEvaluationFunction, IRightsEvaluationScheduler } from "./IRightsEvaluationScheduler";
import { RightType } from "./RightTypes";

interface IRunningEvaluation {
    context: IRightsContext | null;
    groups: IRunningEvaluationGroup[];
}

interface IRunningEvaluationGroup {
    id: number;
    rights: ICancellableControlRightSourceDef[];
    cts: CancellationTokenSource;
    deferred: Deferred<void>;
    finished?: boolean;
}

interface ICancellableControlRightSourceDef {
    type: RightType;
    cts: CancellationTokenSource;
}

/**
 * Implementace plánovače vyhodnocení práv, která se snaží optimalizovat náročný proces vyhodnocení.
 * Pokud přijde požadavek na zaplánování nového vyhodnocení a už probíhá nějaké, které vyhodnocuje nějaké stejné právo s nově požadovanými,
 * tak staré vyhodnocení přeruší a spustí se nové s přidanými právy z minulého. Obě vyhodnocení jsou dokončena až když jsou dokončena jimi požadovaná práva.
 * @internal
 */
export class CancelRunningRightsEvaluationScheduler implements IRightsEvaluationScheduler {
    private static _nextEvaluationId = 0;

    private readonly _logger: ISectionLogger;
    private readonly _id: string;
    private _runningEvaluations: IRunningEvaluation[] = [];
    private _disposed?: boolean;

    constructor(id: string, @DIContainer.instance.bindType(ISectionLogger) logger: ISectionLogger) {
        this._id = id;
        this._logger = logger;
    }

    public schedule(rightTypes: RightType[], context: IRightsContext | null, evaluator: IRightsEvaluationFunction): Promise<void> {
        if (!rightTypes || rightTypes.length === 0)
            return Promise.reject(new InvalidArgumentException("rightTypes"));

        const id = CancelRunningRightsEvaluationScheduler._nextEvaluationId++;

        // kopie pole, aby se neměnilo to příchozí a byla zajištěna unikátnost hodnot
        const rights = new Set(rightTypes);

        this._logger.log(() => `[CRRES ${this._id}] [ID${id}] Scheduling evaluation of rights [${EnumUtils.formatEnums(RightType, rightTypes)}] with context ${this._formatContext(context)}`); //`

        // 1) Při požadavku na vyhodnocení práv se zkontroluje kontext právě běžících vyhodnocení.
        //    Pokud se kontext změnil (vyhodnocujeme vůči jiným datům), tak nemá význam nechávat stará vyhodnocení doběhnout a rušíme je.
        //    Kontext null je zatím výjimkou, ten necháváme doběhnout vždy. Netýká se totiž konkrétních dat. Je možné, že toto se v budoucnu změní.
        const noLongerNeededEvaluations = this._runningEvaluations.filter(evaluation => evaluation.context != null && evaluation.context !== context);
        if (noLongerNeededEvaluations.length > 0) {
            noLongerNeededEvaluations.forEach(evaluation => {
                this._logger.log(() => `[CRRES ${this._id}] [ID${id}] Cancelling evaluation of [${evaluation.groups.map(g => g.id).join(",")}] rights [${evaluation.groups.map(g => EnumUtils.formatEnums(RightType, g.rights.map(def => def.type))).join("|")}] with context ${this._formatContext(evaluation.context)} due to different context`); //`
                evaluation.groups.forEach(group => group.cts.cancel());
            });
            ArrayUtils.removeAll(this._runningEvaluations, noLongerNeededEvaluations);
        }

        // 2) Najdou se data běžícího vyhodnocování pro aktuální kontext (případně se založí nová).
        let runningEvaluation = this._runningEvaluations.find(evaluations => evaluations.context === context);
        if (!runningEvaluation) {
            runningEvaluation = { context: context, groups: [] };
            this._runningEvaluations.push(runningEvaluation);
        }

        const groups = runningEvaluation.groups;

        // 3) Zkontroluje se, jestli pro chtěné typy práv už běží nějaká vyhodnocení.
        //    Pokud ano, tak se stará běžící skupina vyhodnocení celá zruší a spustí se vyhodnocení všech práv znova (sjednocení starých a nových práv) s potenciálně novějšími daty.
        //    Staré vyhodnocení pak čeká dokud nejsou najednou dostupné výsledky vyhodnocení všech jeho práv.
        for (const g of groups) {
            if (g.finished)
                continue;

            if (!ArrayUtils.hasIntersectionWithSet(g.rights, rights, (item) => item.type))
                continue;

            this._logger.log(() => `[CRRES ${this._id}] [ID${id}] Cancelling evaluation of [${g.id}] rights [${EnumUtils.formatEnums(RightType, g.rights.map(def => def.type))}] with context '${this._formatContext(context)}' due to intersection with new evaluation`); //`
            for (const oldRight of g.rights) {
                oldRight.cts.cancel();
                rights.add(oldRight.type);
            }
            g.finished = true;
        }

        const cts = new CancellationTokenSource();
        const group: IRunningEvaluationGroup = {
            id: id,
            rights: Array.from(rights).map(type => ({ type: type, cts: new CancellationTokenSource([cts.token]) })),
            cts: cts,
            deferred: PromiseUtils.defer<void>(),
        };
        groups.push(group);

        this._logger.log(() => `[CRRES ${this._id}] [ID${id}] Starting evaluation of rights [${EnumUtils.formatEnums(RightType, group.rights.map(def => def.type))}] with context ${this._formatContext(context)}`); //`
        // 4) Spustí se nové vyhodnocení (obsahující i případná v minulém kroku zrušená vyhodnocení práv).
        evaluator(group.rights.map(def => ({ type: def.type, ct: def.cts.token })), context, cts.token)
            .then(() => this._checkGroupFinishStatus(group, runningEvaluation))
            .catch(error => this._handleGroupError(group, runningEvaluation, error));

        return group.deferred.promise;
    }

    private _checkGroupFinishStatus(group: IRunningEvaluationGroup, runningEvaluation: IRunningEvaluation): void {
        if (group.finished)
            throw new InvalidOperationException("Group finished but it is already marked as finished");

        group.finished = true;
        group.cts.close();

        this._logger.log(() => `[CRRES ${this._id}] [ID${group.id}] Group evaluation of [${group.id}] rights [${EnumUtils.formatEnums(RightType, group.rights.map(def => def.type))}] with context ${this._formatContext(runningEvaluation.context)} finished`); //`

        for (let i = 0; i < runningEvaluation.groups.length; i++) {
            const g = runningEvaluation.groups[i];
            if (!g.finished)
                continue;

            if (this._checkRightsFinished(g.rights.map(def => def.type), runningEvaluation.groups)) {
                this._logger.log(() => `[CRRES ${this._id}] [ID${group.id}] Group evaluation of [${g.id}] rights [${EnumUtils.formatEnums(RightType, g.rights.map(def => def.type))}] with context ${this._formatContext(runningEvaluation.context)} resolved`); //`
                g.deferred.resolve();
                runningEvaluation.groups.splice(i--, 1);
            }
        }
    }

    private _checkRightsFinished(rightsTypes: RightType[], contextGroups: IRunningEvaluationGroup[]): boolean {
        let rights: RightType[] = rightsTypes;

        for (const g of contextGroups) {
            if (!g.finished)
                continue;

            // odstraní se všechny typy práv, které už jsou v aktuální doběhnuté skupině nezrušené (a tedy vyhodnocené)
            rights = ArrayUtils.removeIf(rights, r => g.rights.some(gr => gr.type === r && !gr.cts.token.cancellationRequested));
        }

        return rights.length === 0;
    }

    private _handleGroupError(group: IRunningEvaluationGroup, runningEvaluation: IRunningEvaluation, error: Error): void {
        //if (error instanceof CancelError)

        this._logger.log(() => `[CRRES ${this._id}] [ID${group.id}] Group evaluation of rights [${EnumUtils.formatEnums(RightType, group.rights.map(def => def.type))}] with context ${ this._formatContext(runningEvaluation.context)} ended with error ${error}`); //`
        ArrayUtils.remove(runningEvaluation.groups, group);
        group.deferred.reject(error);
    }

    private _formatContext(context: IRightsContext | null): string {
        return context ? (context as any).id : null;
    }

    public dispose(): void {
        if (this._disposed)
            return;

        this._disposed = true;
        this._runningEvaluations.forEach(evaluation => evaluation.groups.forEach(group => group.cts.cancel()));
        this._runningEvaluations = [];
    }
}
